import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import { getTeacherAssignmentsState } from "./";
import { getMeta } from "./state";
import * as meta from "./state/meta";
import * as classAssignmentsFetchSelectors from "./state/meta/classAssignments";
import * as individualAssignmentSelectors from "./state/meta/individualAssignments";

type State = any;
type AssignmentId = string;
type ClassId = string;

function getMetaState(state: State) {
  return getMeta(getTeacherAssignmentsState(state));
}

// == Class Future Assignments ==
function getClassAssignmentsMetaState(state: State) {
  return meta.getClassAssignmentsMetaState(getMetaState(state));
}

function getAllClassAssignmentsMetaState(state: State) {
  return meta.getAllClassAssignmentsMetaState(getMetaState(state));
}

// == Fetch all future assignments ==
export const classAssignmentsAreFetching: (
  arg0: State,
  arg1: ClassId
) => boolean = composeSelector(
  getClassAssignmentsMetaState,
  classAssignmentsFetchSelectors.classAssignmentsAreFetching
);

export const classAssignmentsFetchSucceeded: (
  arg0: State,
  arg1: ClassId
) => boolean = composeSelector(
  getClassAssignmentsMetaState,
  classAssignmentsFetchSelectors.classAssignmentsFetchSucceeded
);

export const classAssignmentsFetchErrored: (
  arg0: State,
  arg1: ClassId
) => boolean = composeSelector(
  getClassAssignmentsMetaState,
  classAssignmentsFetchSelectors.classAssignmentsFetchErrored
);

export const classAssignmentsFetchFinished: (
  arg0: State,
  arg1: ClassId
) => boolean = composeSelector(
  getClassAssignmentsMetaState,
  classAssignmentsFetchSelectors.classAssignmentsFetchFinished
);

export const classAssignmentsNeedFetching: (
  arg0: State,
  arg1: ClassId
) => boolean = composeSelector(
  getClassAssignmentsMetaState,
  classAssignmentsFetchSelectors.classAssignmentsNeedFetching
);

export const makeSelectorWithClassAssignmentFetchSucceeded = (
  selector: (state: any, ...args: any[]) => any
) => {
  return function (state: any, classId: string, ...args: any[]) {
    const fetched = classAssignmentsFetchSucceeded(state, classId);

    if (!fetched) {
      return null;
    }

    return selector(state, classId, ...args);
  };
};

// ==  Fetch All Assignments (Past) ==

export const allClassAssignmentsAreFetching: (
  arg0: State,
  arg1: ClassId
) => boolean = composeSelector(
  getAllClassAssignmentsMetaState,
  classAssignmentsFetchSelectors.classAssignmentsAreFetching
);

export const allClassAssignmentsFetchSucceeded: (
  arg0: State,
  arg1: ClassId
) => boolean = composeSelector(
  getAllClassAssignmentsMetaState,
  classAssignmentsFetchSelectors.classAssignmentsFetchSucceeded
);

export const allClassAssignmentsFetchErrored: (
  arg0: State,
  arg1: ClassId
) => boolean = composeSelector(
  getAllClassAssignmentsMetaState,
  classAssignmentsFetchSelectors.classAssignmentsFetchErrored
);

export const allClassAssignmentsFetchFinished: (
  arg0: State,
  arg1?: ClassId
) => boolean = composeSelector(
  getAllClassAssignmentsMetaState,
  classAssignmentsFetchSelectors.classAssignmentsFetchFinished
);

export const allClassAssignmentsNeedFetching: (
  arg0: State,
  arg1: ClassId
) => boolean = composeSelector(
  getAllClassAssignmentsMetaState,
  classAssignmentsFetchSelectors.classAssignmentsNeedFetching
);

export const makeSelectorWithAllClassAssignmentFetchSucceeded = (
  selector: (state: any, ...args: any[]) => any
) => {
  return function (state: any, classId: string, ...args: any[]) {
    const fetched = allClassAssignmentsFetchSucceeded(state, classId);

    if (!fetched) {
      return null;
    }

    return selector(state, classId, ...args);
  };
};

// == Teacher Assignment ==
function getCrudMetaState(state: State) {
  return meta.getCrudMetaState(getMetaState(state));
}

// create
export const assignmentIsBeingCreated: (arg0: State, arg1: ClassId) => boolean =
  composeSelector(
    getCrudMetaState,
    individualAssignmentSelectors.assignmentIsBeingCreated
  );

export const assignmentCreateSucceeded: (
  arg0: State,
  arg1: ClassId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.assignmentCreateSucceeded
);

export const assignmentCreateErrored: (arg0: State, arg1: ClassId) => boolean =
  composeSelector(
    getCrudMetaState,
    individualAssignmentSelectors.assignmentCreateErrored
  );

export const getAssignmentCreateErrorMessage: (
  arg0: State,
  arg1: ClassId
) => string | undefined = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.assignmentCreateErrorMessage
);

// update
export const assignmentIsBeingUpdated: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.assignmentIsBeingUpdated
);

export const assignmentUpdateSucceeded: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.assignmentUpdateSucceeded
);

export const assignmentUpdateErrored: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.assignmentUpdateErrored
);

export const getAssignmentUpdateErrorMessage: (
  arg0: State,
  arg1: ClassId
) => string | undefined = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.assignmentUpdateErrorMessage
);

// delete
export const assignmentIsBeingDeleted: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.assignmentIsBeingDeleted
);

export const assignmentDeleteSucceeded: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.assignmentDeleteSucceeded
);

export const assignmentDeleteErrored: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.assignmentDeleteErrored
);

// fetch existing assignment by Id
export const classAssignmentIsFetching: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.classAssignmentIsFetching
);

export const classAssignmentFetchSucceeded: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.classAssignmentFetchSucceeded
);

export const classAssignmentFetchErrored: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.classAssignmentFetchErrored
);

export const classAssignmentFetchFinished: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.classAssignmentFetchFinished
);

export const classAssignmentNeedsFetching: (
  arg0: State,
  arg1: AssignmentId
) => boolean = composeSelector(
  getCrudMetaState,
  individualAssignmentSelectors.classAssignmentNeedsFetching
);
